import { c as _c } from "react/compiler-runtime";
import { clearSsoReauthPending, getPendingSsoReauth, githubReconnectLogin } from "@/lib/github-reauth";
import type { SsoBlockedOrg } from "@/hooks/useConnectedGithubOrgs";
import { useEffect } from "react";
import { toast } from "sonner";
const TOAST_ID = "sso-reconnect";

/**
 * Prompt the user to finish reconnecting GitHub after authorizing SAML SSO.
 *
 * Exported separately so Storybook can render the exact production toast.
 */
export function showSsoReconnectToast(onReconnect: () => void = githubReconnectLogin): void {
  toast("Authorized on GitHub?", {
    id: TOAST_ID,
    description: "Reconnect to refresh your access.",
    duration: Infinity,
    action: {
      label: "Reconnect",
      onClick: onReconnect
    },
    cancel: {
      label: "Dismiss",
      onClick: () => clearSsoReauthPending()
    }
  });
}

/**
 * Watches for the user returning to the dashboard tab after clicking an org's
 * SSO "Authorize" link, and prompts them to complete the second half of the
 * recovery: a silent OAuth re-login that re-authorizes Blacksmith's token for
 * the org(s) they just established SAML sessions for.
 *
 * Pass `undefined` while org data is still loading: we can't yet tell whether
 * any orgs are blocked, so the pending flag must be left alone (a full page
 * reload after clicking Authorize would otherwise wipe it before data loads).
 */
export function useSsoReauthPrompt(ssoBlockedOrgs) {
  const $ = _c(4);
  const isLoaded = ssoBlockedOrgs !== undefined;
  const hasBlockedOrgs = (ssoBlockedOrgs?.length ?? 0) > 0;
  let t0;
  let t1;
  if ($[0] !== hasBlockedOrgs || $[1] !== isLoaded) {
    t0 = () => {
      if (!isLoaded) {
        return;
      }
      if (!hasBlockedOrgs) {
        clearSsoReauthPending();
        toast.dismiss(TOAST_ID);
        return;
      }
      const maybePrompt = _temp;
      maybePrompt();
      window.addEventListener("focus", maybePrompt);
      document.addEventListener("visibilitychange", maybePrompt);
      return () => {
        window.removeEventListener("focus", maybePrompt);
        document.removeEventListener("visibilitychange", maybePrompt);
      };
    };
    t1 = [isLoaded, hasBlockedOrgs];
    $[0] = hasBlockedOrgs;
    $[1] = isLoaded;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
}
function _temp() {
  if (document.visibilityState !== "visible") {
    return;
  }
  if (!getPendingSsoReauth()) {
    return;
  }
  showSsoReconnectToast();
}